/**
* LocalePillarLinks — every locale homepage links to all pillar pages in the
* SAME language (Rulebook §9: no orphan locale pillars).
*
* The 12 pillars below exist for all 6 non-Thai locales, so the same list is
* safe to render for every locale prefix.
*/
export type SupportedLocale = "en" | "zh" | "ja" | "ko" | "vi" | "id";

const PILLARS = [
  "notary",
  "lgz",
  "pcc",
  "moj",
  "sworn",
  "naati",
  "visa",
  "longstay",
  "marriage",
  "corporate",
  "ip",
  "store",
] as const;

type Pillar = (typeof PILLARS)[number];

const HEADING: Record<SupportedLocale, string> = {
  en: "All services in English",
  zh: "全部服务（中文）",
  ja: "日本語のサービス一覧",
  ko: "한국어 서비스 전체",
  vi: "Toàn bộ dịch vụ (Tiếng Việt)",
  id: "Semua layanan (Bahasa Indonesia)",
};

const LABELS: Record<SupportedLocale, Record<Pillar, string>> = {
  en: {
    notary: "Notary Public",
    lgz: "Legalization & Apostille",
    pcc: "Police Clearance Certificate",
    moj: "Ministry of Justice documents",
    sworn: "Sworn translator",
    naati: "NAATI certified translation",
    visa: "Visa support",
    longstay: "Long-stay visa",
    marriage: "Marriage & family",
    corporate: "Company registration",
    ip: "Intellectual property",
    store: "Store",
  },
  zh: {
    notary: "公证服务",
    lgz: "使馆认证与海牙认证",
    pcc: "无犯罪记录证明",
    moj: "司法部文件",
    sworn: "宣誓翻译",
    naati: "NAATI 认证翻译",
    visa: "签证支持",
    longstay: "长期居留签证",
    marriage: "结婚与家事",
    corporate: "公司注册",
    ip: "知识产权",
    store: "商城",
  },
  ja: {
    notary: "公証サービス",
    lgz: "大使館認証・アポスティーユ",
    pcc: "無犯罪証明書",
    moj: "法務省関連書類",
    sworn: "宣誓翻訳",
    naati: "NAATI認定翻訳",
    visa: "ビザサポート",
    longstay: "長期滞在ビザ",
    marriage: "結婚・家族関連",
    corporate: "会社設立",
    ip: "知的財産",
    store: "ストア",
  },
  ko: {
    notary: "공증 서비스",
    lgz: "대사관 인증·아포스티유",
    pcc: "범죄경력증명서",
    moj: "법무부 서류",
    sworn: "선서 번역",
    naati: "NAATI 공인 번역",
    visa: "비자 지원",
    longstay: "장기 체류 비자",
    marriage: "결혼·가족",
    corporate: "법인 설립",
    ip: "지식재산권",
    store: "스토어",
  },
  vi: {
    notary: "Công chứng (Notary Public)",
    lgz: "Hợp pháp hóa & Apostille",
    pcc: "Lý lịch tư pháp",
    moj: "Hồ sơ Bộ Tư pháp",
    sworn: "Biên dịch tuyên thệ",
    naati: "Dịch thuật NAATI",
    visa: "Hỗ trợ visa",
    longstay: "Visa dài hạn",
    marriage: "Kết hôn & gia đình",
    corporate: "Thành lập công ty",
    ip: "Sở hữu trí tuệ",
    store: "Cửa hàng",
  },
  id: {
    notary: "Notary Public",
    lgz: "Legalisasi & Apostille",
    pcc: "Surat Keterangan Catatan Kepolisian",
    moj: "Dokumen Kementerian Kehakiman",
    sworn: "Penerjemah tersumpah",
    naati: "Terjemahan bersertifikat NAATI",
    visa: "Dukungan visa",
    longstay: "Visa tinggal jangka panjang",
    marriage: "Pernikahan & keluarga",
    corporate: "Pendirian perusahaan",
    ip: "Kekayaan intelektual",
    store: "Toko",
  },
};

export function LocalePillarLinks({ locale }: { locale: SupportedLocale }) {
  const labels = LABELS[locale];
  return (
    <section className="border-t border-border bg-muted/30">
      <nav aria-label={HEADING[locale]} className="mx-auto max-w-6xl px-4 py-12">
        <h2 className="text-xl font-bold text-foreground">{HEADING[locale]}</h2>
        <ul className="mt-5 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p) => (
            <li key={p}>
              <a href={`/${locale}/${p}`} className="text-[var(--gold)] hover:underline">
                {labels[p]}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </section>
  );
}
